/** "My Log" — everything this contributor has recorded, and where it stands. */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useRefreshOnFocus } from "../hooks/useRefreshOnFocus";
import { RefreshButton } from "../components/RefreshButton";
import { Layout } from "../components/Layout";
import { PublishPill } from "../components/StatusPill";
import { ClipPlayer } from "../components/ClipPlayer";
import { IconArrow, IconCheck, IconClock, IconInfo, IconMic, IconRefresh } from "../components/Icons";
import { earnings as earningsApi, recordings as recordingsApi } from "../api/endpoints";
import { formatDate } from "../lib/validation";
import type { RecordingCard, RecordingStatus } from "../types/api";

const FILTERS: Array<{ id: RecordingStatus | "all"; label: string }> = [
  { id: "all", label: "All" },
  { id: "pending_approval", label: "In review" },
  { id: "approved", label: "Approved" },
  { id: "rerecord", label: "Re-record" },
  { id: "rejected", label: "Not accepted" },
];

export function Log() {
  const [items, setItems] = useState<RecordingCard[]>([]);
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState<RecordingStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [counts, setCounts] = useState<Partial<Record<RecordingStatus | "all", number>>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [result, summary] = await Promise.all([
        recordingsApi.mine({
          status: filter === "all" ? undefined : filter,
          search: search.trim() || undefined,
        }),
        earningsApi.summary().catch(() => null),
      ]);
      setItems(result.recordings);
      setTotal(result.total);
      if (summary) {
        setCounts({
          all: summary.recordings_total,
          pending_approval: summary.recordings_pending,
          approved: summary.recordings_approved,
          rerecord: summary.recordings_rerecord,
          rejected: summary.recordings_rejected,
        });
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  // Debounced so typing in the search box does not fire a request per key.
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), search ? 350 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  // A review verdict can land while this page sits open, so pick it up on
  // the way back to the tab.
  useRefreshOnFocus(() => void load(), { enabled: !loading });

  const emptyLibrary = !search && filter === "all" && total === 0;

  return (
    <Layout title="My Log">
      <section className="page log-page">
        <div className="log-head">
          <div>
            <div className="page-kicker">MY LOG</div>
            <h1>Your recordings</h1>
            <div className="log-count">
              {loading ? "\u00a0" : `${total} voice ${total === 1 ? "note" : "notes"}`}
            </div>
          </div>
          <RefreshButton onClick={() => void load()} busy={loading} />
        </div>

        <div className="log-toolbar">
          <div className="log-filters" role="tablist" aria-label="Filter recordings">
            {FILTERS.map((entry) => (
              <button
                key={entry.id}
                type="button"
                className={filter === entry.id ? "on" : ""}
                onClick={() => setFilter(entry.id)}
              >
                {entry.label}
                {counts[entry.id] != null ? ` ${counts[entry.id]}` : ""}
              </button>
            ))}
          </div>
          <label className="log-search">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
              <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
              <path d="M10.5 10.5 14 14" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            <input
              value={search}
              placeholder="Search recordings"
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search recordings"
            />
          </label>
        </div>

        <p className="log-legend">
          <span><i className="is-review" /> In review <em>Waiting for review</em></span>
          <span><i className="is-approved" /> Approved <em>Qualifies for payment</em></span>
          <span><i className="is-rerecord" /> Re-record <em>Needs another take</em></span>
          <span><i className="is-rejected" /> Not accepted <em>Didn't meet guidelines</em></span>
        </p>

        {error && <div className="form-error">{error}</div>}

        {loading && (
          <div className="log-list" aria-busy="true" aria-label="Loading your log">
            {[0, 1, 2].map((row) => (
              <div key={row} className="log-card log-skeleton">
                <div className="skeleton-line skeleton-title" />
                <div className="skeleton-line skeleton-meta" />
                <div className="skeleton-line skeleton-text" />
              </div>
            ))}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="log-empty">
            <div className="log-empty-mic" aria-hidden="true"><IconMic width={22} height={22} /></div>
            <h2>No recordings yet</h2>
            <p>
              {emptyLibrary
                ? "Your submitted voice notes will appear here."
                : "Nothing matches this filter."}
            </p>
            {emptyLibrary && (
              <Link to="/record" className="btn btn-primary log-start">
                <span className="start-long">Start a new record</span>
                <span className="start-short">Start recording</span>
                <span className="start-long" aria-hidden="true"><IconArrow width={16} height={16} /></span>
                <span className="start-short" aria-hidden="true"><IconMic width={16} height={16} /></span>
              </Link>
            )}
          </div>
        )}

        {!loading && items.length > 0 && (
          <div className="log-list">
            {items.map((item) => (
              <LogCard key={item.recording_id} item={item} />
            ))}
          </div>
        )}
      </section>
    </Layout>
  );
}

function statusCopy(item: RecordingCard): string {
  if (item.review_note && item.status !== "approved") return item.review_note;
  if (item.status === "approved") return item.earned ? "Added to your earnings." : "Approved.";
  if (item.status === "rerecord") return "We couldn't hear your voice clearly.";
  if (item.status === "rejected") return "The recording didn't follow the requested prompt.";
  if (item.status === "pending_approval") return "We're reviewing this recording.";
  return "Replaced by a newer take.";
}

function LogCard({ item }: { item: RecordingCard }) {
  const money = `$${item.compensation.formatted}`;
  return (
    <article className={`log-card is-${item.status}`}>
      <div className="log-card-top">
        <div>
          <h2>{item.title}</h2>
          <p>
            Voice notes #{item.response_number}
            {item.category ? ` · ${item.category}` : ""}
            {" · "}
            {formatDate(item.created_at)}
          </p>
        </div>
        <div className="log-card-side">
          <div className="log-card-badges">
            <StatusMark status={item.status} />
            <PublishPill status={item.publish_status} />
            {item.status === "approved" && <strong className="log-earned">+{money}</strong>}
          </div>
          {item.status === "pending_approval" && (
            <span className="log-potential">Potential: {money}</span>
          )}
        </div>
      </div>

      {item.playback_url ? (
        <ClipPlayer url={item.playback_url} durationSeconds={item.duration_seconds} variant="record" />
      ) : (
        <p className="log-missing">Playback isn't available for this take.</p>
      )}

      <p className="log-card-text">{item.text?.trim() || "Transcript unavailable."}</p>

      <div className="log-card-foot">
        <p>
          <StatusGlyph status={item.status} />
          {statusCopy(item)}
        </p>
        {item.status === "rerecord" && (
          <Link to={`/record?assignment=${item.assignment_id}`} className="btn btn-primary">
            Record again <IconRefresh width={14} height={14} />
          </Link>
        )}
      </div>
    </article>
  );
}

function StatusMark({ status }: { status: RecordingStatus }) {
  const label =
    status === "pending_approval" ? "In review"
    : status === "approved" ? "Approved"
    : status === "rerecord" ? "Re-record"
    : status === "rejected" ? "Not accepted"
    : "Replaced";
  return (
    <span className={`log-status is-${status}`}>
      <StatusGlyph status={status} />
      {label}
    </span>
  );
}

function StatusGlyph({ status }: { status: RecordingStatus }) {
  if (status === "approved") return <IconCheck width={14} height={14} />;
  if (status === "rerecord") return <IconRefresh width={14} height={14} />;
  if (status === "rejected") return <IconInfo width={14} height={14} />;
  return <IconClock width={14} height={14} />;
}
